import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKeyboardLift } from '../hooks/useKeyboardLift';
import { createPortal } from 'react-dom';
import {
  IonPage,
  IonContent,
  IonButton,
  IonIcon,
  IonSpinner,
  IonSkeletonText,
  IonText,
  IonRefresherContent,
  IonGrid,
  IonCard,
  IonCardContent,
  IonBadge,
  IonLabel,
  IonModal,
  IonRow,
  IonCol,
  IonList,
  IonItem,
  IonAvatar,
  IonFab,
  IonSearchbar,
  useIonRouter,
} from '@ionic/react';
import {
  addOutline,
  calendarOutline,
  cashOutline,
  chevronBack,
  chevronDownOutline,
  close,
  constructOutline,
  receiptOutline,
  refreshOutline,
  search as searchIcon,
  swapVertical,
} from 'ionicons/icons';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import PositionedRefresher from '../components/PositionedRefresher';
import { useScrollSnap } from '../hooks/useScrollSnap';
import { applySearchOverlayPosition } from './bookingSystem/overlayPosition';
import { listServiceOrders, type ZohoSalesOrder } from '../services/zohoServiceOrderService';
import ServiceOrderDetail from './ServiceOrderDetail';
import CreateInvoice from './CreateInvoice';
import { getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import styles from './EstimateList.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { translateDynamic } from '../i18n/dynamic';


interface ServiceOrderListProps {
  onClose?: () => void;
}

type StatusFilter = 'all' | 'draft' | 'open' | 'invoiced' | 'void' | 'cancelled';
type SortField = 'date' | 'amount' | 'number' | 'customer';
type SortDirection = 'asc' | 'desc';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', get label() { return i18n.t('pages.ServiceOrderList.allOrders'); } },
  { value: 'draft', get label() { return i18n.t('pages.ServiceOrderList.draft'); } },
  { value: 'open', get label() { return i18n.t('pages.ServiceOrderList.open'); } },
  { value: 'invoiced', get label() { return i18n.t('pages.ServiceOrderList.invoiced'); } },
  { value: 'void', get label() { return i18n.t('pages.ServiceOrderList.void'); } },
  { value: 'cancelled', get label() { return i18n.t('pages.ServiceOrderList.cancelled'); } },
];

const SORT_OPTIONS: Array<{ value: SortField; label: string }> = [
  { value: 'date', get label() { return i18n.t('pages.ServiceOrderList.orderDate'); } },
  { value: 'amount', get label() { return i18n.t('pages.ServiceOrderList.amount'); } },
  { value: 'number', get label() { return i18n.t('pages.ServiceOrderList.orderNumber'); } },
  { value: 'customer', get label() { return i18n.t('pages.ServiceOrderList.customerName'); } },
];

type PortalDropdownRect = { top: number; left?: number; right?: number; width?: number };

function usePortalDropdownPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  align: 'start' | 'end' = 'start',
) {
  const [rect, setRect] = useState<PortalDropdownRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;

    const computeRect = (): PortalDropdownRect | null => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      const top = box.bottom - anchor.top + 4;

      if (align === 'end') {
        return { top, right: anchor.right - box.right };
      }

      return { top, left: box.left - anchor.left, width: box.width };
    };

    const applyRect = (r: PortalDropdownRect) => {
      const card = cardRef.current;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.right = r.right !== undefined ? `${r.right}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) {
          setRect(r);
          applyRect(r);
        }
      });
    });

    const handleScrollPosition = () => {
      const r = computeRect();
      if (r) applyRect(r);
    };

    const handleResize = () => {
      const r = computeRect();
      if (r) {
        setRect(r);
        applyRect(r);
      }
    };

    const handleOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('scroll', handleScrollPosition, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      document.removeEventListener('scroll', handleScrollPosition, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, anchorRef, portalRef, cardRef, onClose, align]);

  return rect;
}

const PortalDropdown: React.FC<{
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  options: Array<{ label: string; value: string; sublabel?: string }>;
  value?: string;
  onSelect: (value: string) => void;
  align?: 'start' | 'end';
}> = ({ isOpen, anchorRef, portalRef, onClose, options, value, onSelect, align = 'start' }) => {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePortalDropdownPosition(isOpen, anchorRef, portalRef, cardRef, onClose, align);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);

  if (!isOpen || !rect || !portalNode) return null;

  return createPortal(
    <IonCard ref={cardRef} className={styles.customDropdownList} style={{ ...rect, position: 'absolute' }}>
      <IonList lines="none" className={styles.customDropdownListInner}>
        {options.map((option) => (
          <IonItem
            key={option.value}
            button
            lines="none"
            detail={false}
            className={`${styles.customDropdownItem} ${option.value === value ? styles.customDropdownItemActive : ''}`}
            onClick={() => onSelect(option.value)}
          >
            <IonLabel>{option.label}</IonLabel>
            {option.sublabel && (
              <IonText slot="end" className={styles.customDropdownSublabel}>
                {option.sublabel}
              </IonText>
            )}
          </IonItem>
        ))}
      </IonList>
    </IonCard>,
    portalNode,
  );
};

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value || 0);
}

function formatDate(value?: string) {
  if (!value) return translateDynamic('Unknown');
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function getStatusTone(status: string) {
  switch (status.toLowerCase()) {
    case 'invoiced':
      return styles.badgeSuccess;
    case 'open':
      return styles.badgePrimary;
    case 'void':
    case 'cancelled':
      return styles.badgeDanger;
    default:
      return styles.badgeMedium;
  }
}

const ServiceOrderList: React.FC<ServiceOrderListProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const contentRef = useRef<HTMLIonGridElement>(null);

  const [orders, setOrders] = useState<ZohoSalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchText, setSearchText] = useState('');
  // null = no active server search (or it hasn't resolved yet) — see the
  // debounced search effect below, same pattern as InvoiceList.tsx.
  const [searchResults, setSearchResults] = useState<ZohoSalesOrder[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const filterTriggerRef = useRef<HTMLIonButtonElement>(null);
  const sortTriggerRef = useRef<HTMLIonButtonElement>(null);
  const pickerPortalRef = useRef<HTMLIonGridElement>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  // Lift the floating search bar above the native keyboard (same as the other search bars)
  useKeyboardLift({ bars: () => [overlayRef.current] });

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentRef, 66, applyProgress);

  // Mirrors InvoiceList.tsx's loadInvoices(reset): reset=true reloads page 1
  // (replacing `orders`), reset=false loads the next page and appends —
  // triggered by the near-bottom scroll check further down. `silent` skips the
  // full-page spinner for background refreshes (pull-to-refresh, closing detail).
  const loadOrders = useCallback(async (reset = true, silent = false) => {
    try {
      if (reset) {
        if (!silent) setLoading(true);
        setError(null);
      } else {
        setIsLoadingMore(true);
      }
      // Read the page to fetch via the setter's functional form (rather
      // than closing over the `page` state value) so this callback's
      // identity doesn't change every time the page advances — that would
      // otherwise make the mount effect below re-run on every pagination
      // step too.
      let targetPage = 1;
      if (!reset) {
        setPage(prev => { targetPage = prev + 1; return prev; });
      }
      const { orders: data, hasMore: more } = await listServiceOrders(undefined, targetPage);
      if (reset) {
        setOrders(data);
      } else {
        setOrders(prev => {
          const seen = new Set(prev.map(o => o.salesorder_id));
          return [...prev, ...data.filter(o => !seen.has(o.salesorder_id))];
        });
      }
      setHasMore(more);
      setPage(targetPage);
    } catch (err) {
      console.error('[ServiceOrderList] failed to load service orders', err);
      if (reset) setError(t('pages.ServiceOrderList.unableToLoadServiceOrdersRight'));
    } finally {
      if (reset && !silent) setLoading(false);
      if (!reset) setIsLoadingMore(false);
    }
  }, [t]);

  useEffect(() => {
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — loadOrders's own setLoading(true)/setError(null)
    // are needed on every real invocation (including pull-to-refresh and
    // pagination), so they can't just be dropped.
    Promise.resolve().then(() => loadOrders(true));
  }, [loadOrders]);

  // Debounced server-side search — reaches past whatever pages are currently
  // loaded since it queries Zoho directly instead of filtering `orders`.
  // Clearing results for a too-short query is computed during render
  // (React's "adjust state when a dependency changes" pattern) rather than
  // as a synchronous setState call at the top of the effect.
  const [prevSearchTextForResults, setPrevSearchTextForResults] = useState(searchText);
  if (searchText !== prevSearchTextForResults) {
    setPrevSearchTextForResults(searchText);
    if (searchText.trim().length < 2) setSearchResults(null);
  }
  useEffect(() => {
    if (searchText.trim().length < 2) return;
    const timer = window.setTimeout(async () => {
      try {
        const { orders: results } = await listServiceOrders(searchText.trim());
        setSearchResults(results);
      } catch (err) {
        console.error('[ServiceOrderList] search failed', err);
        setSearchResults([]);
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  const filteredOrders = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    const base = needle ? (searchResults ?? orders) : orders;
    return base.filter((order) => {
      const status = String(order.status || '').toLowerCase();
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!needle) return true;
      // While the debounced server search hasn't resolved yet, still filter
      // the locally-loaded list so results don't just vanish for a beat.
      if (searchResults !== null && base === searchResults) return true;

      return [order.salesorder_number, order.customer_name, order.reference_number, order.status]
        .some((value) => String(value || '').toLowerCase().includes(needle));
    });
  }, [orders, searchResults, searchText, statusFilter]);

  const sortedOrders = useMemo(() => {
    const items = [...filteredOrders];
    const direction = sortDirection === 'asc' ? 1 : -1;
    items.sort((a, b) => {
      switch (sortField) {
        case 'amount':
          return (Number(a.total || 0) - Number(b.total || 0)) * direction;
        case 'number':
          return String(a.salesorder_number || '').localeCompare(String(b.salesorder_number || '')) * direction;
        case 'customer':
          return String(a.customer_name || '').localeCompare(String(b.customer_name || '')) * direction;
        case 'date':
        default:
          return String(a.date || '').localeCompare(String(b.date || '')) * direction;
      }
    });
    return items;
  }, [filteredOrders, sortDirection, sortField]);

  const handleRefresh = async (event: CustomEvent) => {
    await loadOrders(true);
    event.detail.complete();
  };

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push('/tabs/manage');
    }
  }, [onClose, router]);

  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection('desc');
  }, [sortField]);

  const openOrderDetail = useCallback((order: ZohoSalesOrder) => {
    const id = String(order.salesorder_id || '').trim();
    if (!id) return;
    setSelectedOrderId(id);
  }, []);

  const closeOrderDetail = useCallback(() => {
    setSelectedOrderId(null);
    loadOrders(true, true);
  }, [loadOrders]);

  const updateOverlayPosition = useCallback(() => {
    applySearchOverlayPosition(overlayRef.current, isSearchVisible);
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  const handleSearchToggle = useCallback(() => {
    if (isSearchVisible) {
      setSearchText('');
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => {
        setIsSearchHidden(true);
        setIsSearchClosing(false);
      }, 220);
      return;
    }

    setIsSearchHidden(false);
    setIsSearchVisible(true);
    setTimeout(() => {
      searchRef.current?.setFocus();
    }, 40);
  }, [isSearchVisible]);

  const content = (
    <>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={t('pages.ServiceOrderList.serviceOrders')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <>
            <IonButton shape="round" className={styles.closeButton} onClick={handleClose}>
              <IonIcon icon={chevronBack} />
            </IonButton>
            <IonButton shape="round" className={styles.closeButton} style={{ visibility: 'hidden' }}>
              <IonIcon icon={chevronBack} />
            </IonButton>
          </>
        }
        endButtons={
          <>
            <IonButton shape="round" className={styles.newInvoiceBtn} onClick={() => setSelectedOrderId('new')}>
              <IonIcon icon={addOutline} />
            </IonButton>
            <IonButton shape="round" className={styles.searchButton} onClick={handleSearchToggle}>
              <IonIcon icon={isSearchVisible ? close : searchIcon} />
            </IonButton>
          </>
        }
      >
        <IonContent scrollY={false} className={styles.content}>
          <PositionedRefresher
            titleRef={titleCardAnimationRef}
            contentRef={contentRef}
            offset={46}
            anchor="bottom"
            pullMin={120}
            pullMax={200}
            pullFactor={0.6}
            onIonRefresh={handleRefresh}
            style={{
              '--ion-color-step-250': 'var(--ion-background-color)',
              '--ion-color-step-200': 'var(--ion-background-color)',
              '--ion-background-color': 'transparent',
            } as React.CSSProperties}
          >
            <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
          </PositionedRefresher>

          <IonGrid
            ref={contentRef}
            className={styles.container}
            onScroll={(e) => {
              const el = e.target as HTMLElement;
              handleScroll(el.scrollTop || 0);
              // Load more when near bottom — no pagination while an active
              // search is in flight, since that already fetches up to 200 in one shot.
              if (hasMore && !isLoadingMore && !loading && !searchText.trim()) {
                const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
                if (nearBottom) loadOrders(false);
              }
            }}
          >
            <TitleCard
              ref={titleCardAnimationRef}
              title={t('pages.ServiceOrderList.serviceOrders')}
              cardClassName={styles.titleCard}
              titleContainerClassName={styles.titleContainer}
              titleClassName={styles.titleText}
            />

            <IonGrid className={styles.filterBar}>
              <IonButton
                ref={filterTriggerRef}
                fill="clear"
                className={styles.periodBtn}
                onClick={() => setShowFilterDropdown((current) => !current)}
              >
                {STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label || t('pages.ServiceOrderList.allOrders')}
                <IonIcon icon={chevronDownOutline} />
              </IonButton>

              <IonButton
                ref={sortTriggerRef}
                fill="clear"
                className={`${styles.periodBtn} ${styles.sortTrigger}`}
                onClick={() => setShowSortDropdown((current) => !current)}
              >
                <IonIcon icon={swapVertical} />
              </IonButton>
            </IonGrid>

            <IonGrid ref={pickerPortalRef} className={styles.pickerPortalRoot} />

            <PortalDropdown
              isOpen={showFilterDropdown}
              anchorRef={filterTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setShowFilterDropdown(false)}
              value={statusFilter}
              onSelect={(value) => {
                setStatusFilter(value as StatusFilter);
                setShowFilterDropdown(false);
              }}
              options={STATUS_OPTIONS.map((option) => ({ label: option.label, value: option.value }))}
            />

            <PortalDropdown
              isOpen={showSortDropdown}
              anchorRef={sortTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setShowSortDropdown(false)}
              align="end"
              value={sortField}
              onSelect={(value) => {
                handleSort(value as SortField);
                setShowSortDropdown(false);
              }}
              options={SORT_OPTIONS.map((option) => ({
                label: option.label,
                value: option.value,
                sublabel: sortField === option.value ? (sortDirection === 'asc' ? '↑' : '↓') : undefined,
              }))}
            />

            {loading ? (
              <IonList className={styles.mobileList} lines="none" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <IonCard key={`skeleton-${i}`} className={`${styles.card} ${styles.skeletonCard}`}>
                    <IonCardContent>
                      <IonItem lines="none" className={styles.cardTopItem}>
                        <IonSkeletonText animated slot="start" className={`${styles.skeletonBar} ${styles.cardAvatar}`} style={{ borderRadius: '50%' }} />
                        <IonLabel>
                          <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '55%', height: 16, margin: 0 }} />
                        </IonLabel>
                        <IonSkeletonText animated slot="end" className={styles.skeletonBar} style={{ width: 64, height: 20, margin: 0, borderRadius: 10 }} />
                      </IonItem>
                      <IonGrid className={styles.statsRow}>
                        <IonRow>
                          {[0, 1, 2].map((j) => (
                            <IonCol key={j} className={styles.statCol}>
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 16, height: 16, margin: 0, borderRadius: 4 }} />
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '65%', height: 12, margin: '1px 0' }} />
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '40%', height: 10, margin: '1px 0' }} />
                            </IonCol>
                          ))}
                        </IonRow>
                      </IonGrid>
                      {/* Real cards show a "Ref:" line under the stats (0.74rem text + 8px top margin) */}
                      <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '45%', height: 12, margin: '8px 0 2px' }} />
                    </IonCardContent>
                  </IonCard>
                ))}
              </IonList>
            ) : error ? (
              <IonGrid className={styles.emptyState}>
                <IonIcon icon={refreshOutline} className={styles.stateIcon} />
                <h3>{t('pages.ServiceOrderList.unableToLoadServiceOrders')}</h3>
                <p>{error}</p>
                <IonButton fill="clear" className={styles.stateAction} onClick={() => loadOrders()}>{t('pages.ServiceOrderList.tryAgain')}
                </IonButton>
              </IonGrid>
            ) : sortedOrders.length === 0 ? (
              <IonGrid className={styles.emptyState}>
                <IonIcon icon={constructOutline} size="large" color="medium" />
                <h3>{t('pages.ServiceOrderList.noServiceOrdersFound')}</h3>
                <p>{t('pages.ServiceOrderList.tryAdjustingYourSearchOrFilters')}</p>
              </IonGrid>
            ) : (
              <IonList className={styles.mobileList} lines="none">
                {sortedOrders.map((order) => (
                  <IonCard
                    key={order.salesorder_id}
                    className={styles.card}
                    button
                    onClick={() => openOrderDetail(order)}
                  >
                    <IonCardContent>
                      <IonItem lines="none" className={styles.cardTopItem}>
                        <IonAvatar slot="start" className={styles.cardAvatar}>
                          <img
                            src={getDefaultAvatar(order.customer_name || '')}
                            alt={order.customer_name || ''}
                          />
                        </IonAvatar>
                        <IonLabel>
                          <h2 className={styles.cardName}>{order.customer_name || t('pages.ServiceOrderList.unknownCustomer')}</h2>
                        </IonLabel>
                        <IonBadge className={`${styles.badge} ${getStatusTone(order.status || '')}`} slot="end">
                          {translateDynamic(order.status || 'draft')}
                        </IonBadge>
                      </IonItem>

                      <IonGrid className={styles.statsRow}>
                        <IonRow>
                          <IonCol className={styles.statCol}>
                            <IonIcon icon={cashOutline} className={`${styles.statIcon} ${styles.statIconGreen}`} />
                            <IonText>
                              <p className={styles.statValue}>{formatCurrency(Number(order.total || 0))}</p>
                            </IonText>
                            <IonText>
                              <p className={styles.statMeta}>{t('pages.ServiceOrderList.amount')}</p>
                            </IonText>
                          </IonCol>

                          <IonCol className={styles.statCol}>
                            <IonIcon icon={receiptOutline} className={`${styles.statIcon} ${styles.statIconBlue}`} />
                            <IonText>
                              <p className={styles.statValue}>{order.salesorder_number || 'N/A'}</p>
                            </IonText>
                            <IonText>
                              <p className={styles.statMeta}>{t('pages.ServiceOrderList.order')}</p>
                            </IonText>
                          </IonCol>

                          <IonCol className={`${styles.statCol} ${styles.statColLast}`}>
                            <IonIcon icon={calendarOutline} className={`${styles.statIcon} ${styles.statIconPurple}`} />
                            <IonText>
                              <p className={styles.statValue}>{formatDate(order.date)}</p>
                            </IonText>
                            <IonText>
                              <p className={styles.statMeta}>{t('pages.ServiceOrderList.orderDate')}</p>
                            </IonText>
                          </IonCol>
                        </IonRow>
                      </IonGrid>

                      {order.reference_number && (
                        <IonText className={styles.referenceText}>{t('pages.ServiceOrderList.ref', { reference_number: order.reference_number })}</IonText>
                      )}
                    </IonCardContent>
                  </IonCard>
                ))}
              </IonList>
            )}

            {isLoadingMore && (
              <IonGrid className={styles.loadMoreSpinner}>
                <IonSpinner name="bubbles" color="primary" />
              </IonGrid>
            )}
            {!hasMore && !searchText.trim() && sortedOrders.length > 0 && (
              <IonText className={styles.endOfList}>{t('pages.ServiceOrderList.allServiceOrdersLoaded')}</IonText>
            )}

            {isSearchVisible && <IonGrid className={styles.searchBarSpacer} />}
          </IonGrid>
        </IonContent>

        <IonFab
          ref={(element) => {
            overlayRef.current = element as unknown as HTMLElement;
          }}
          className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
          aria-hidden={!isSearchVisible}
        >
          <IonSearchbar
            ref={searchRef}
            value={searchText}
            debounce={80}
            onIonInput={(e) => setSearchText(e.detail.value ?? '')}
            onIonClear={() => setSearchText('')}
            placeholder={t('pages.ServiceOrderList.searchServiceOrders', { length: orders.length })}
            animated
            showCancelButton="never"
            showClearButton={searchText ? 'always' : 'never'}
            className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
          />
        </IonFab>
      </BasePageWithHeader>

      {selectedOrderId !== null && (
        <IonModal isOpen={selectedOrderId !== null} onDidDismiss={closeOrderDetail} handle={false}>
          {selectedOrderId === 'new' ? (
            <CreateInvoice
              onClose={() => setSelectedOrderId(null)}
              onCreated={closeOrderDetail}
              prefill={{ invoiceType: 'ORDER', lineItems: [] }}
            />
          ) : (
            <ServiceOrderDetail salesorderId={selectedOrderId} onClose={closeOrderDetail} />
          )}
        </IonModal>
      )}
    </>
  );

  return onClose
    ? <div className={styles.modalWrapper}>{content}</div>
    : <IonPage className={styles.page}>{content}</IonPage>;
};

export default ServiceOrderList;
